import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Fade from '@mui/material/Fade';

// アイコン
import BusinessIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentIcon from '@mui/icons-material/Assignment';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useAuth } from '../auth/AuthContext';

// タブコンポーネントのインポート
import StoreManagementTab from '../components/management/StoreManagementTab';
import AnnouncementManagementTab from '../components/management/AnnouncementManagementTab';
import SystemSettingsTab from '../components/management/SystemSettingsTab';
import StoreApplicationTab from '../components/management/StoreApplicationTab';

// タブパネル
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`management-tabpanel-${index}`}
      aria-labelledby={`management-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          <Fade in={value === index} timeout={300}>
            <div>{children}</div>
          </Fade>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `management-tab-${index}`,
    'aria-controls': `management-tabpanel-${index}`,
  };
}

const ManagementSettings: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { storeId } = useAuth();
  // 基本状態
  const [tabValue, setTabValue] = useState(0);

  // タブ変更
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h1" style={{ fontWeight: 600 }}>
          運営設定
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : undefined}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<BusinessIcon />}
            label="店舗管理"
            {...a11yProps(0)}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <Tab
            icon={<CampaignIcon />}
            label="お知らせ管理"
            {...a11yProps(1)}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <Tab
            icon={<AdminPanelSettingsIcon />}
            label="システム共通設定"
            {...a11yProps(2)}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <Tab
            icon={<AssignmentIcon />}
            label="店舗申込情報"
            {...a11yProps(3)}
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Tabs>
      </Paper>

      {/* 店舗管理タブ */}
      <TabPanel value={tabValue} index={0}>
        <StoreManagementTab />
      </TabPanel>

      {/* お知らせ管理タブ */}
      <TabPanel value={tabValue} index={1}>
        <AnnouncementManagementTab />
      </TabPanel>

      {/* システム設定タブ */}
      <TabPanel value={tabValue} index={2}>
        <SystemSettingsTab storeId={storeId || ''} />
      </TabPanel>

      {/* 店舗申込情報タブ */}
      <TabPanel value={tabValue} index={3}>
        <StoreApplicationTab />
      </TabPanel>
    </Box>
  );
};

export default ManagementSettings;
